/**
 * Sidebar Detail Component
 * 
 * Accordion-style collapsible section for the right sidebar.
 * Based on reference: app/src/views/private/components/sidebar-detail.vue
 */

import { useState, ReactNode } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface SidebarDetailProps {
    icon: ReactNode;
    title: string;
    badge?: boolean | string | number;
    close?: boolean;
    defaultOpen?: boolean;
    onClose?: () => void;
    children: ReactNode;
    className?: string;
}

export function SidebarDetail({
    icon,
    title,
    badge,
    close = false,
    defaultOpen = false,
    onClose,
    children,
    className,
}: SidebarDetailProps) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className={cn("border-b border-border", className)}>
            {/* Toggle Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex w-full items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <span className="text-muted-foreground">{icon}</span>
                        {!!badge && (
                            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                                {typeof badge === 'boolean' ? '' : badge}
                            </span>
                        )}
                    </div>
                    <span className="font-medium text-sm">{title}</span>
                </div>
                <div className="flex items-center gap-2">
                    {close && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose?.();
                            }}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                    {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                </div>
            </button>

            {/* Content */}
            {isOpen && (
                <div className="px-4 py-3 bg-muted/20">
                    {children}
                </div>
            )}
        </div>
    );
}
